import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtUsd } from '../../lib/formatHelpers';

/**
 * V2 CommissionBreakdownPanel — Détail des contributions à un payout/commission.
 * Rendu à l'intérieur d'un PaymentRowCard expandé.
 *
 * Affiche :
 *  - une ligne récapitulative haute (total brut, total net, etc. — si dispo)
 *  - un tableau (ou liste mobile) des contributions par créateur :
 *      Créateur · Diamonds · Commission · Status
 *
 * NoIRock R2 : amounts AES — affichés "—" + badge AES quand non décrypté.
 *
 * Props :
 *  - breakdown    : array<{ creator_id, creator_name, diamonds, amount, commission, status }>
 *  - creatorBasePath : string ("/admin/v2/creators" ou "/manager/team")
 *  - summary      : { totalAmount, totalCommission, count } (optional)
 *  - loading      : bool
 */
const CommissionBreakdownPanel = ({
  breakdown = [],
  creatorBasePath = '/admin/v2/creators',
  summary,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">
              {t('paymentsV2.breakdown.totalAmount', 'Total amount')}
            </div>
            <div className="font-heading font-bold text-base tabular-nums mt-0.5">
              {summary.totalAmount != null ? fmtUsd(summary.totalAmount, { decimals: 2 }) : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">
              {t('paymentsV2.breakdown.totalCommission', 'Total commission')}
            </div>
            <div className="font-heading font-bold text-base tabular-nums mt-0.5">
              {summary.totalCommission != null ? fmtUsd(summary.totalCommission, { decimals: 2 }) : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">
              {t('paymentsV2.breakdown.creators', 'Creators')}
            </div>
            <div className="font-heading font-bold text-base tabular-nums mt-0.5">
              {fmtNum(summary.count ?? breakdown.length)}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-32 skeleton rounded" />
      ) : breakdown.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          {t('paymentsV2.breakdown.empty', 'No detail rows available for this period.')}
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    {t('paymentsV2.breakdown.colCreator', 'Creator')}
                  </th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    {t('paymentsV2.breakdown.colDiamonds', 'Diamonds')}
                  </th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    {t('paymentsV2.breakdown.colAmount', 'Amount')}
                  </th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    {t('paymentsV2.breakdown.colCommission', 'Commission')}
                  </th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    {t('paymentsV2.breakdown.colStatus', 'Status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((r) => (
                  <tr key={r.creator_id || r.id} className="border-b border-border last:border-b-0 hover:bg-muted/40">
                    <td className="px-4 py-2">
                      {r.creator_id ? (
                        <Link to={`${creatorBasePath}/${r.creator_id}`} className="text-foreground hover:text-primary transition-colors">
                          {r.creator_name || '—'}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">{r.creator_name || '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.diamonds != null ? fmtNum(r.diamonds) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.amount != null ? fmtUsd(r.amount, { decimals: 2 }) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.commission != null ? fmtUsd(r.commission, { decimals: 2 }) : '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {r.status || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile list */}
          <div className="md:hidden space-y-2">
            {breakdown.map((r) => (
              <div key={r.creator_id || r.id} className="rounded-xl border border-border p-3 bg-background/30">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {r.creator_id ? (
                      <Link to={`${creatorBasePath}/${r.creator_id}`} className="font-medium text-foreground hover:text-primary truncate block">
                        {r.creator_name || '—'}
                      </Link>
                    ) : (
                      <div className="font-medium text-muted-foreground truncate">{r.creator_name || '—'}</div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {r.diamonds != null ? fmtNum(r.diamonds) + ' ♦' : ''}
                      {r.status ? ` · ${r.status}` : ''}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold tabular-nums">
                      {r.amount != null ? fmtUsd(r.amount, { decimals: 2 }) : '—'}
                    </div>
                    {r.commission != null && (
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        {fmtUsd(r.commission, { decimals: 2 })} comm.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CommissionBreakdownPanel;
